"use strict";
/**
 * Swiss Ephemeris Type Definitions - Flag Utilities
 *
 * This file contains utilities for working with bitwise flags in a type-safe manner.
 * These builder classes make it easier to combine flags while maintaining type safety.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EclipseTypeFlags = exports.CalculationFlags = void 0;
exports.normalizeFlags = normalizeFlags;
exports.normalizeEclipseTypes = normalizeEclipseTypes;
const enums_js_1 = require("./enums.js");
/**
 * Type-safe flag builder for calculation flags
 *
 * @example
 * const flags = CalculationFlags
 *   .from(CalculationFlag.SwissEphemeris)
 *   .add(CalculationFlag.Speed)
 *   .add(CalculationFlag.Equatorial);
 *
 * const position = calculatePosition(jd, Planet.Sun, flags);
 */
class CalculationFlags {
    constructor(initialFlags) {
        this.flags = 0;
        if (initialFlags !== undefined) {
            this.add(initialFlags);
        }
    }
    /**
     * Add one or more flags to the current set
     * @param flag - Single flag or array of flags to add
     * @returns this (for method chaining)
     */
    add(flag) {
        if (Array.isArray(flag)) {
            flag.forEach((f) => (this.flags |= f));
        }
        else {
            this.flags |= flag;
        }
        return this;
    }
    /**
     * Remove one or more flags from the current set
     * @param flag - Single flag or array of flags to remove
     * @returns this (for method chaining)
     */
    remove(flag) {
        if (Array.isArray(flag)) {
            flag.forEach((f) => (this.flags &= ~f));
        }
        else {
            this.flags &= ~flag;
        }
        return this;
    }
    /**
     * Check if a specific flag is set
     * @param flag - Flag to check
     * @returns true if the flag is set
     */
    has(flag) {
        return (this.flags & flag) === flag;
    }
    /**
     * Convert to raw number for passing to C library
     * @returns The numeric representation of all combined flags
     */
    toNumber() {
        return this.flags;
    }
    /**
     * Create a new CalculationFlags instance from one or more flags
     * @param flags - Flags to combine
     * @returns New CalculationFlags instance
     */
    static from(...flags) {
        return new CalculationFlags(flags);
    }
    /**
     * Common preset: Swiss Ephemeris with speed calculation
     */
    static get swissEphemerisWithSpeed() {
        return CalculationFlags.from(enums_js_1.CalculationFlag.SwissEphemeris, enums_js_1.CalculationFlag.Speed);
    }
    /**
     * Common preset: Moshier ephemeris with speed calculation
     */
    static get moshierWithSpeed() {
        return CalculationFlags.from(enums_js_1.CalculationFlag.MoshierEphemeris, enums_js_1.CalculationFlag.Speed);
    }
    /**
     * Common preset: Astrometric positions (no aberration or light deflection)
     */
    static get astrometric() {
        return CalculationFlags.from(enums_js_1.CalculationFlag.SwissEphemeris, enums_js_1.CalculationFlag.NoAberration, enums_js_1.CalculationFlag.NoGravitationalDeflection);
    }
    /**
     * Common preset: Heliocentric positions
     */
    static get heliocentric() {
        return CalculationFlags.from(enums_js_1.CalculationFlag.SwissEphemeris, enums_js_1.CalculationFlag.Heliocentric);
    }
    /**
     * Common preset: Topocentric positions
     */
    static get topocentric() {
        return CalculationFlags.from(enums_js_1.CalculationFlag.SwissEphemeris, enums_js_1.CalculationFlag.Topocentric);
    }
    /**
     * Common preset: Equatorial coordinates (RA/Dec)
     */
    static get equatorial() {
        return CalculationFlags.from(enums_js_1.CalculationFlag.SwissEphemeris, enums_js_1.CalculationFlag.Equatorial, enums_js_1.CalculationFlag.Speed);
    }
}
exports.CalculationFlags = CalculationFlags;
/**
 * Type-safe flag builder for eclipse type filtering
 *
 * @example
 * const eclipseFilter = EclipseTypeFlags
 *   .from(EclipseType.Total)
 *   .add(EclipseType.Partial);
 *
 * const eclipse = findNextLunarEclipse(jd, flags, eclipseFilter.toNumber());
 */
class EclipseTypeFlags {
    constructor(initialFlags) {
        this.flags = 0;
        if (initialFlags !== undefined) {
            this.add(initialFlags);
        }
    }
    /**
     * Add one or more eclipse types to the filter
     * @param flag - Single type or array of types to add
     * @returns this (for method chaining)
     */
    add(flag) {
        if (Array.isArray(flag)) {
            flag.forEach((f) => (this.flags |= f));
        }
        else {
            this.flags |= flag;
        }
        return this;
    }
    /**
     * Check if a specific eclipse type is in the filter
     * @param flag - Eclipse type to check
     * @returns true if the type is included
     */
    has(flag) {
        return (this.flags & flag) === flag;
    }
    /**
     * Convert to raw number for passing to C library
     * @returns The numeric representation of all combined types
     */
    toNumber() {
        return this.flags;
    }
    /**
     * Create a new EclipseTypeFlags instance from one or more types
     * @param flags - Eclipse types to combine
     * @returns New EclipseTypeFlags instance
     */
    static from(...flags) {
        return new EclipseTypeFlags(flags);
    }
    /**
     * Preset: All solar eclipse types
     */
    static get allSolar() {
        return new EclipseTypeFlags([
            enums_js_1.EclipseType.Central,
            enums_js_1.EclipseType.NonCentral,
            enums_js_1.EclipseType.Total,
            enums_js_1.EclipseType.Annular,
            enums_js_1.EclipseType.Partial,
            enums_js_1.EclipseType.AnnularTotal,
        ]);
    }
    /**
     * Preset: All lunar eclipse types
     */
    static get allLunar() {
        return new EclipseTypeFlags([
            enums_js_1.EclipseType.Total,
            enums_js_1.EclipseType.Partial,
            enums_js_1.EclipseType.Penumbral,
        ]);
    }
    /**
     * Preset: Only total eclipses
     */
    static get totalOnly() {
        return EclipseTypeFlags.from(enums_js_1.EclipseType.Total);
    }
    /**
     * Preset: Total and partial eclipses (no penumbral)
     */
    static get totalAndPartial() {
        return EclipseTypeFlags.from(enums_js_1.EclipseType.Total, enums_js_1.EclipseType.Partial);
    }
}
exports.EclipseTypeFlags = EclipseTypeFlags;
/**
 * Normalize calculation flag input to a number
 * This allows functions to accept multiple flag formats
 *
 * @param input - Flags in various formats
 * @returns Numeric representation of the flags
 */
function normalizeFlags(input) {
    if (typeof input === 'number') {
        return input;
    }
    if (input instanceof CalculationFlags) {
        return input.toNumber();
    }
    if (Array.isArray(input)) {
        return CalculationFlags.from(...input).toNumber();
    }
    // Single CalculationFlag enum value
    return input;
}
/**
 * Normalize eclipse type flag input to a number
 *
 * @param input - Eclipse types in various formats
 * @returns Numeric representation of the types
 */
function normalizeEclipseTypes(input) {
    if (typeof input === 'number') {
        return input;
    }
    if (input instanceof EclipseTypeFlags) {
        return input.toNumber();
    }
    if (Array.isArray(input)) {
        return EclipseTypeFlags.from(...input).toNumber();
    }
    // Single EclipseType enum value
    return input;
}
